import * as DialogPrimitive from '@radix-ui/react-dialog';
import type { ReactNode } from 'react';
import { Button } from './Button';
import { closeOnlyByButtons } from './dialogGuards';
import { Icon } from './Icon';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  /** 머리 제목 (Heading 20/B) — 스크린리더 이름으로도 쓴다 */
  title: string;
  /** 하단 오른쪽 버튼 영역 (취소/확인 등) */
  footer?: ReactNode;
  children: ReactNode;
}

/**
 * Figma Modal — 가운데 520px 흰 카드(radius 16) + 40% 딤.
 * 제목 줄 오른쪽 × · 취소 버튼으로만 닫힌다(딤 클릭 · ESC 로는 닫히지 않는다).
 */
export function Modal({ open, onClose, title, footer, children }: ModalProps) {
  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-dim" />
        <DialogPrimitive.Content
          {...closeOnlyByButtons}
          aria-describedby={undefined}
          className="fixed top-1/2 left-1/2 z-50 flex max-h-[calc(100dvh-32px)] w-[520px] max-w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2 flex-col gap-5 overflow-y-auto rounded-16 bg-surface-white px-5 pt-6 pb-5 outline-none sm:px-7 sm:pt-7 sm:pb-6"
        >
          <div className="flex w-full items-center justify-between gap-4">
            <DialogPrimitive.Title className="text-heading-20 font-bold text-fg-primary">
              {title}
            </DialogPrimitive.Title>
            <DialogPrimitive.Close asChild>
              <Button variant="ghost" size="icon-sm" aria-label="닫기">
                <Icon name="close" size={16} />
              </Button>
            </DialogPrimitive.Close>
          </div>
          {children}
          {footer && (
            <div className="flex w-full justify-end gap-2">{footer}</div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

interface ModalFieldProps {
  /** Body 14/M · tertiary */
  label: string;
  /** 아래 보조 문구 (Body 13/R · light) */
  hint?: ReactNode;
  children: ReactNode;
}

/** 모달 안의 "라벨 / 입력 / 보조 문구" 묶음 */
export function ModalField({ label, hint, children }: ModalFieldProps) {
  return (
    <div className="flex w-full flex-col gap-2">
      <span className="text-body-14 font-medium text-fg-tertiary">{label}</span>
      {children}
      {hint && (
        <p className="text-body-13 font-regular text-fg-light">{hint}</p>
      )}
    </div>
  );
}
